import React, { useRef, useState } from 'react';
import { Sparkles, ListTodo, CalendarPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnchoredMenu } from '../../../components/ui/AnchoredMenu';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { Email, EmailPersonalFolderTreeNode, EmailTag } from '../useData';

function flattenPersonalFolders(
  nodes: EmailPersonalFolderTreeNode[],
  depth = 0,
): { id: number; name: string; depth: number }[] {
  const out: { id: number; name: string; depth: number }[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, depth });
    if (node.children?.length) {
      out.push(...flattenPersonalFolders(node.children, depth + 1));
    }
  }
  return out;
}

export interface EmailDetailToolbarProps {
  email: Email;
  onBack?: () => void;
  commentsOpen: boolean;
  onToggleComments: () => void;
  toolbarMenuOpen: boolean;
  onToolbarMenuOpenChange: (open: boolean) => void;
  replyDropdownOpen: boolean;
  onReplyDropdownOpenChange: (open: boolean) => void;
  onToggleStar: () => void;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onMarkAsRead: () => void;
  onMarkAsUnread: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onLinkClient: () => void;
  onLinkTodo: () => void;
  onLinkAppointment: () => void;
  onCopyToFolder?: (folderId: number) => void;
  personalFolders?: EmailPersonalFolderTreeNode[];
  emailTags?: EmailTag[];
  onAddTag: (tagId: number) => void;
  onRemoveTag: (tagId: number) => void;
  showAiAssist?: boolean;
  onOpenAiAssist?: () => void;
  showAiDraft?: boolean;
  onDraftTodo?: () => void;
  onDraftAppointment?: () => void;
  aiDraftLoading?: 'todo' | 'appointment' | null;
  hasMessageHeaders?: boolean;
  onViewHeaders?: () => void;
  onSaveAsEml: () => void;
  /** Admin-only: reimport MIME from an .eml file (Message-ID must match). */
  onReimportEml?: () => void;
  isReimportingEml?: boolean;
  onEditAsNew: () => void;
  onPrint: () => void;
  onMarkSpam: (blockSender: boolean) => void;
  onMarkNotSpam?: (unblockSender: boolean) => void;
  isMarkingRead?: boolean;
  isArchiving?: boolean;
  isMarkingSpam?: boolean;
  isMarkingNotSpam?: boolean;
}

const EmailDetailToolbar: React.FC<EmailDetailToolbarProps> = ({
  email,
  onBack,
  commentsOpen,
  onToggleComments,
  toolbarMenuOpen,
  onToolbarMenuOpenChange,
  replyDropdownOpen,
  onReplyDropdownOpenChange,
  onToggleStar,
  onReply,
  onReplyAll,
  onForward,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onUnarchive,
  onLinkClient,
  onLinkTodo,
  onLinkAppointment,
  onCopyToFolder,
  personalFolders = [],
  emailTags = [],
  onAddTag,
  onRemoveTag,
  showAiAssist,
  onOpenAiAssist,
  showAiDraft,
  onDraftTodo,
  onDraftAppointment,
  aiDraftLoading,
  hasMessageHeaders,
  onViewHeaders,
  onSaveAsEml,
  onReimportEml,
  isReimportingEml,
  onEditAsNew,
  onPrint,
  onMarkSpam,
  onMarkNotSpam,
  isMarkingRead,
  isArchiving,
  isMarkingSpam,
  isMarkingNotSpam,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const replyBtnRef = useRef<HTMLButtonElement>(null);
  const linkBtnRef = useRef<HTMLButtonElement>(null);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const tagBtnRef = useRef<HTMLButtonElement>(null);
  const aiBtnRef = useRef<HTMLButtonElement>(null);
  const toolbarMenuBtnRef = useRef<HTMLButtonElement>(null);
  const [linkMenuOpen, setLinkMenuOpen] = useState(false);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);

  const closeMenu = () => onToolbarMenuOpenChange(false);
  const hasLinks = Boolean(email.linkedClient || email.linkedTodo || email.linkedAppointment);
  const folderOptions = flattenPersonalFolders(personalFolders);
  const showCopyButton = Boolean(onCopyToFolder && folderOptions.length > 0);
  const showTagButton = emailTags.length > 0;
  const hasTagsOnEmail = (email.tags?.length ?? 0) > 0;

  const menuItemClass = cn(
    'w-full text-left px-3 py-2 text-sm flex items-center gap-2',
    colors.text,
    colors.interactiveHover,
  );

  const iconDropdownBtnClass = (active: boolean, accent?: 'indigo' | 'emerald') =>
    cn(
      'h-9 min-w-[2.25rem] pl-2 pr-1.5 rounded-lg flex items-center justify-center gap-0.5 transition-colors',
      active && accent === 'emerald'
        ? 'text-emerald-600 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:hover:bg-emerald-900/60'
        : active || (accent === 'indigo' && hasLinks)
          ? 'text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/60'
          : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
    );

  return (
    <div className={cn('email-detail-toolbar relative z-30 flex-shrink-0 border-b print:hidden overflow-visible', colors.border, colors.headerBg)}>
      <div className="flex flex-wrap items-center justify-between gap-1.5 px-2 py-1.5 min-w-0">
        <div className="flex items-center gap-1 shrink-0 min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className={cn(
                'h-9 w-9 rounded-lg flex items-center justify-center transition-colors',
                'text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
              )}
              title="Back to list"
              aria-label="Back to list"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={onToggleStar}
            className={cn(
              'h-9 w-9 rounded-lg flex items-center justify-center transition-colors',
              email.isStarred
                ? 'text-amber-500 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-900/20'
                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
            )}
            title={email.isStarred ? 'Unstar' : 'Star'}
          >
            <svg className="w-5 h-5" fill={email.isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>

          <div className="relative inline-block">
            <button
              ref={linkBtnRef}
              type="button"
              onClick={() => setLinkMenuOpen((v) => !v)}
              className={iconDropdownBtnClass(linkMenuOpen || hasLinks, 'indigo')}
              title="Link to client, todo or appointment"
              aria-expanded={linkMenuOpen}
              aria-haspopup="menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <AnchoredMenu
              open={linkMenuOpen}
              onClose={() => setLinkMenuOpen(false)}
              anchorRef={linkBtnRef}
              align="start"
              className={cn('min-w-[180px]', colors.surface, colors.border)}
            >
              <button
                type="button"
                onClick={() => { setLinkMenuOpen(false); onLinkClient(); }}
                className={menuItemClass}
              >
                <span className="text-indigo-600">●</span>
                Link to organization
              </button>
              <button
                type="button"
                onClick={() => { setLinkMenuOpen(false); onLinkTodo(); }}
                className={menuItemClass}
              >
                <span className="text-teal-600">●</span>
                Link to Todo
              </button>
              <button
                type="button"
                onClick={() => { setLinkMenuOpen(false); onLinkAppointment(); }}
                className={menuItemClass}
              >
                <span className="text-sky-600">●</span>
                Link to Appointment
              </button>
            </AnchoredMenu>
          </div>

          {showCopyButton && (
            <div className="relative inline-block">
              <button
                ref={copyBtnRef}
                type="button"
                onClick={() => setCopyMenuOpen((v) => !v)}
                className={iconDropdownBtnClass(copyMenuOpen, 'emerald')}
                title="Copy to folder"
                aria-expanded={copyMenuOpen}
                aria-haspopup="menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <AnchoredMenu
                open={copyMenuOpen}
                onClose={() => setCopyMenuOpen(false)}
                anchorRef={copyBtnRef}
                align="start"
                className={cn('min-w-[180px] max-h-[240px] overflow-y-auto', colors.surface, colors.border)}
              >
                {folderOptions.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => {
                      setCopyMenuOpen(false);
                      onCopyToFolder?.(f.id);
                    }}
                    className={menuItemClass}
                    style={{ paddingLeft: 12 + f.depth * 16 }}
                  >
                    <svg className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    {f.name}
                  </button>
                ))}
              </AnchoredMenu>
            </div>
          )}

          {showTagButton && (
            <div className="relative inline-block">
              <button
                ref={tagBtnRef}
                type="button"
                onClick={() => setTagMenuOpen((v) => !v)}
                className={iconDropdownBtnClass(tagMenuOpen || hasTagsOnEmail)}
                title="Tags"
                aria-expanded={tagMenuOpen}
                aria-haspopup="menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <AnchoredMenu
                open={tagMenuOpen}
                onClose={() => setTagMenuOpen(false)}
                anchorRef={tagBtnRef}
                align="start"
                className={cn('min-w-[180px] max-h-[280px] overflow-y-auto', colors.surface, colors.border)}
              >
                {hasTagsOnEmail && (
                  <>
                    <div className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                      On this email
                    </div>
                    {email.tags?.map((t) => {
                      const tagMeta = emailTags.find((et) => et.id === t.id);
                      const canEditTag = !tagMeta || tagMeta.isOwner !== false || tagMeta.permission === 'write';
                      return (
                        <div key={t.id} className="flex items-center gap-1 px-2">
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={t.color ? { backgroundColor: t.color } : undefined}
                          />
                          <span className={cn('flex-1 truncate text-sm py-1.5', colors.text)}>{t.name}</span>
                          {canEditTag && (
                            <button
                              type="button"
                              onClick={() => onRemoveTag(t.id)}
                              className={cn('p-1 rounded', colors.textMuted, colors.interactiveHover)}
                              title="Remove tag"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div className={cn('border-t my-1', colors.divide)} />
                  </>
                )}
                <div className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Add tag
                </div>
                {emailTags
                  .filter((t) => !email.tagIds?.includes(t.id))
                  .filter((t) => t.isOwner !== false || t.permission === 'write')
                  .map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setTagMenuOpen(false);
                        onAddTag(t.id);
                      }}
                      className={menuItemClass}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={t.color ? { backgroundColor: t.color } : { backgroundColor: '#94a3b8' }}
                      />
                      <span className="text-indigo-600 dark:text-indigo-400">+</span>
                      {t.name}
                    </button>
                  ))}
                {emailTags.filter((t) => !email.tagIds?.includes(t.id)).length === 0 && (
                  <p className={cn('px-3 py-2 text-xs', colors.textMuted)}>All tags added</p>
                )}
              </AnchoredMenu>
            </div>
          )}

          {showAiDraft && onDraftTodo && onDraftAppointment && (
            <div className="relative inline-block">
              <button
                ref={aiBtnRef}
                type="button"
                onClick={() => setAiMenuOpen((v) => !v)}
                disabled={aiDraftLoading != null}
                className={iconDropdownBtnClass(aiMenuOpen, 'indigo')}
                title="AI actions"
                aria-expanded={aiMenuOpen}
                aria-haspopup="menu"
                aria-label="AI actions"
              >
                <Sparkles className="w-5 h-5" aria-hidden />
                <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <AnchoredMenu
                open={aiMenuOpen}
                onClose={() => setAiMenuOpen(false)}
                anchorRef={aiBtnRef}
                align="start"
                className={cn('min-w-[200px]', colors.surface, colors.border)}
              >
                <button
                  type="button"
                  onClick={() => {
                    setAiMenuOpen(false);
                    onDraftTodo();
                  }}
                  disabled={aiDraftLoading != null}
                  className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
                >
                  <ListTodo className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  {aiDraftLoading === 'todo' ? 'Drafting todo…' : 'Draft Todo'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAiMenuOpen(false);
                    onDraftAppointment();
                  }}
                  disabled={aiDraftLoading != null}
                  className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
                >
                  <CalendarPlus className="w-4 h-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
                  {aiDraftLoading === 'appointment' ? 'Drafting appointment…' : 'Draft Appointment'}
                </button>
              </AnchoredMenu>
            </div>
          )}

          {!email.isRead && (
            <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" aria-hidden title="Unread" />
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <div className="relative inline-block">
            <button
              ref={replyBtnRef}
              type="button"
              onClick={() => onReplyDropdownOpenChange(!replyDropdownOpen)}
              className={cn(
                'h-9 px-2.5 rounded-lg flex items-center gap-1 transition-colors text-sm font-medium',
                replyDropdownOpen
                  ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200'
                  : 'text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40',
              )}
              title="Reply"
              aria-expanded={replyDropdownOpen}
              aria-haspopup="menu"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <span className="hidden sm:inline">Reply</span>
              <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <AnchoredMenu
              open={replyDropdownOpen}
              onClose={() => onReplyDropdownOpenChange(false)}
              anchorRef={replyBtnRef}
              align="end"
              className={cn('min-w-[160px]', colors.surface, colors.border)}
            >
              <button type="button" onClick={onReply} className={menuItemClass}>
                Reply
              </button>
              <button type="button" onClick={onReplyAll} className={menuItemClass}>
                Reply to All
              </button>
            </AnchoredMenu>
          </div>

          <button
            type="button"
            onClick={onForward}
            className="h-9 px-2.5 rounded-lg flex items-center gap-1 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
            title="Forward"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            <span className="hidden sm:inline">Forward</span>
          </button>

          <button
            type="button"
            onClick={onToggleComments}
            className={cn(
              'h-9 w-9 rounded-lg flex items-center justify-center transition-colors',
              commentsOpen || email.hasComments || (email.commentCount ?? 0) > 0
                ? 'text-indigo-600 bg-indigo-100/80 hover:bg-indigo-200/80 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50'
                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
            )}
            title={t('comments')}
            aria-label={t('comments')}
            aria-pressed={commentsOpen}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>

          <div className="relative inline-block">
            <button
              ref={toolbarMenuBtnRef}
              type="button"
              onClick={() => onToolbarMenuOpenChange(!toolbarMenuOpen)}
              className={cn(
                'h-9 w-9 rounded-lg flex items-center justify-center transition-colors',
                toolbarMenuOpen
                  ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                  : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
              )}
              title="More actions"
              aria-label="More actions"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
            <AnchoredMenu
              open={toolbarMenuOpen}
              onClose={closeMenu}
              anchorRef={toolbarMenuBtnRef}
              align="end"
              className={cn('min-w-[220px] max-h-[min(70vh,28rem)] overflow-y-auto', colors.surface, colors.border)}
            >
              <button
                type="button"
                onClick={() => { closeMenu(); onMarkAsRead(); }}
                disabled={email.isRead || isMarkingRead}
                className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
              >
                Mark as read
              </button>
              <button
                type="button"
                onClick={() => { closeMenu(); onMarkAsUnread(); }}
                disabled={!email.isRead}
                className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
              >
                Mark as unread
              </button>
              <button
                type="button"
                onClick={() => { closeMenu(); onArchive(); }}
                disabled={email.isArchived || isArchiving}
                className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
              >
                Archive
              </button>
              <button
                type="button"
                onClick={() => { closeMenu(); onUnarchive(); }}
                disabled={!email.isArchived || isArchiving}
                className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
              >
                Unarchive
              </button>

              {showAiAssist && onOpenAiAssist && (
                <>
                  <div className={cn('border-t my-1', colors.divide)} />
                  <button
                    type="button"
                    onClick={() => { closeMenu(); onOpenAiAssist(); }}
                    className={cn(menuItemClass, 'text-indigo-700 dark:text-indigo-300')}
                  >
                    <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
                    AI Assistant
                  </button>
                </>
              )}

              <div className={cn('border-t my-1', colors.divide)} />
              <button type="button" onClick={onSaveAsEml} className={menuItemClass}>Save as .eml</button>
              {onReimportEml && (
                <button
                  type="button"
                  onClick={() => { closeMenu(); onReimportEml(); }}
                  disabled={isReimportingEml}
                  className={cn(menuItemClass, 'disabled:opacity-50 disabled:cursor-not-allowed')}
                  title="Admin: replace body and attachments from an .eml file (Message-ID must match)"
                >
                  {isReimportingEml ? 'Reimporting…' : 'Reimport from .eml…'}
                </button>
              )}
              <button type="button" onClick={onEditAsNew} className={menuItemClass}>Edit as new</button>
              <button type="button" onClick={onPrint} className={menuItemClass}>Print</button>
              {hasMessageHeaders && onViewHeaders && (
                <button type="button" onClick={() => { closeMenu(); onViewHeaders(); }} className={menuItemClass}>
                  View message headers
                </button>
              )}
              {email.folder === 'JUNK' && onMarkNotSpam && (
                <>
                  <div className={cn('border-t my-1', colors.divide)} />
                  <button
                    type="button"
                    onClick={() => { closeMenu(); onMarkNotSpam(false); }}
                    disabled={isMarkingNotSpam}
                    className={cn(menuItemClass, 'text-emerald-700 dark:text-emerald-400 disabled:opacity-50')}
                  >
                    {isMarkingNotSpam ? 'Moving…' : 'Not spam'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { closeMenu(); onMarkNotSpam(true); }}
                    disabled={isMarkingNotSpam}
                    className={cn(menuItemClass, 'text-emerald-700 dark:text-emerald-400 disabled:opacity-50')}
                  >
                    {isMarkingNotSpam ? 'Moving…' : 'Not spam & unblock sender'}
                  </button>
                </>
              )}
              {email.folder !== 'JUNK' && (
                <>
                  <div className={cn('border-t my-1', colors.divide)} />
                  <button
                    type="button"
                    onClick={() => { closeMenu(); onMarkSpam(false); }}
                    disabled={isMarkingSpam}
                    className={cn(menuItemClass, 'text-red-600 dark:text-red-400 disabled:opacity-50')}
                  >
                    {isMarkingSpam ? 'Reporting…' : 'Report spam'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { closeMenu(); onMarkSpam(true); }}
                    disabled={isMarkingSpam}
                    className={cn(menuItemClass, 'text-red-600 dark:text-red-400 disabled:opacity-50')}
                  >
                    {isMarkingSpam ? 'Reporting…' : 'Report spam & block sender'}
                  </button>
                </>
              )}
            </AnchoredMenu>
          </div>
        </div>
      </div>

      {(email.tags?.length ?? 0) > 0 && (
        <div className="px-2 pb-1.5 flex flex-wrap items-center gap-1 border-t border-gray-200/80 dark:border-gray-700/80">
          {email.tags?.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              style={t.color ? { backgroundColor: `${t.color}20`, color: t.color } : undefined}
            >
              {t.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailDetailToolbar;
